import { getProperty } from './DebugUtil';
import DebugObject from './DebugObject';

export default class Console {
  constructor() {
    this.init();
  }

  init() {
    this.cacheElement();
    this.setEvent();
  }

  cacheElement() {
    this.welConsoleWrapper = $('#_debug_wrap');
    this.welController = this.welConsoleWrapper.find(
      '#_debug_console_controller'
    );
    this.welConsoleInputWrapper = this.welConsoleWrapper.find(
      '#_debug_console_input'
    );
    this.welConsoleInput = this.welController.find('input');
    this.welGuide = this.welConsoleWrapper.find('#_debug_console_guide');
  }

  setEvent() {
    this.welConsoleInput.on('keyup', e => {
      this.onKeyupControllerInput(e);
    });
    this.welController.on('click', '#_debug_console_action', () => {
      this.onClickControllerAction();
    });
    this.welController.on('click', '#_debug_console_clear', () => {
      this.onClickConsoleClear();
    });

    this.welConsoleWrapper.on('click', '#_debug_console_guide li', e => {
      this.onClickGuide(e);
    });
    this.welConsoleWrapper.on('click', '#_debug_console_preset li', e => {
      this.onClickPreset(e);
    });
  }

  /**
   * 콘솔에 입력시 해당 문자로 시작하는 property들을 보여줌
   * @param e
   */
  onKeyupControllerInput() {
    const sInput = this.welConsoleInput.val();
    this.addGuide(sInput);
  }

  /**
   * console은 window의 메서드를 실행하거나 property를 반환하는 기능만 한다.
   */
  onClickControllerAction() {
    this.removeGuide();

    const sCommand = this.welConsoleInput.val().trim();
    try {
      const { sWarning, result } = this.execute(sCommand);
      this.renderExecutionResult(
        this.welConsoleInputWrapper,
        sCommand,
        sWarning,
        result
      );
    } catch (e) {
      this.renderExecutionResult(this.welConsoleInputWrapper, sCommand, e, e);
    }

    this.resetConsoleInput();
  }

  onClickConsoleClear() {
    this.resetConsoleInput();
    this.welConsoleWrapper.find('._debug_console_box').remove();
  }

  resetConsoleInput() {
    this.welConsoleInput.val('');
  }

  onClickPreset(e) {
    const sItem = $(e.currentTarget).text();
    this.welConsoleInput.val(sItem);
  }

  onClickGuide(e) {
    const sItem = $(e.currentTarget).text();
    const currentValue = this.welConsoleInput.val();
    let { sPath } = this.separateInput(currentValue);
    if (sPath.length) {
      sPath += '.';
    }
    this.welConsoleInput.val(`${sPath}${sItem}`);
    this.removeGuide();
  }

  addGuide(sInput) {
    // 검색결과를 노출할 element
    const welWrapper = this.welGuide.empty().show();

    if (!sInput.length || sInput === '.') {
      // '.'을 .split('.') 하면 ["", ""]가 되므로 제외함
      this.removeGuide();
      return;
    }
    // 입력한 문자열에서 유효한 property를 찾아 보여줌
    const { sPath, sLastCommand: sSearchKey } = this.separateInput(sInput);
    const oParent = this.getObject(sPath);
    const aProperty = this.getProperties(oParent, sSearchKey);

    if (!aProperty) {
      return;
    }

    this.renderGuide(aProperty, welWrapper);
  }

  removeGuide() {
    this.welGuide.hide();
  }

  renderGuide(aProperty, welWrapper) {
    aProperty.forEach(value => {
      welWrapper.append(`<li>${value}</li>`);
    });
  }

  /**
   * 부모 객체 중 해당 키워드로 시작하는 property 들을 배열로 반환한다
   * @param oParent
   * @param sSearchKey
   * @returns {string[]}
   */
  getProperties(oParent, sSearchKey) {
    let aProperty = Object.keys(oParent);
    const sKey = sSearchKey.replace(/\(|\)/g, '');

    aProperty = aProperty.filter(
      key => key.search(new RegExp(`^${sKey}`)) !== -1
    );
    aProperty.sort();

    return aProperty;
  }

  /**
   * path가 유효하면 window에서부터 해당 객체를 찾아 반환하고, 아니면 window 객체를 반환한다
   * @param sPath
   * @returns {Window}
   */
  getObject(sPath) {
    return sPath ? getProperty(window, sPath) : window;
  }

  /**
   * 예시) T, TMON, TMON., TMON.ht
   * .이 있으면 .앞까지 path를 만들고, .이후를 lastCommand로 만든다, .이 없으면 전체가 lastCommand
   * @param sInput
   * @returns {{sPath: *, sLastCommand: *}}
   */
  separateInput(sInput) {
    let sPath;
    let sLastCommand;
    const bHasDot = sInput.indexOf('.') !== -1;

    if (bHasDot) {
      const nLastIndexOfDot = sInput.lastIndexOf('.');
      sPath = sInput.slice(0, nLastIndexOfDot);
      sLastCommand = sInput.slice(nLastIndexOfDot + 1);
    } else {
      sPath = '';
      sLastCommand = sInput;
    }

    return {
      sPath,
      sLastCommand,
    };
  }

  execute(sCommand) {
    const sExecutionType = this.getExecutionType(sCommand);
    let target;
    if (sExecutionType !== 'function') {
      target = getProperty(window, sCommand);
      return {
        sWarning: '',
        result: target,
      };
    }

    // 실행할 메서드와 전달인자를 분리한다
    const sArguments = sCommand.replace(/(.*)\((.*)\)/, '$2');
    const aArguments = sArguments
      ? sArguments.split(',').map(item => item.trim())
      : []; // ()사이의 입력
    const sTargetPath = sCommand.replace(/(.*)\(.*\)/, '$1'); // ()을 제외한 입력
    target = getProperty(window, sTargetPath);
    return this.executeFunction(target, aArguments);
  }

  executeFunction(target, aArguments) {
    const bTargetIsFunction = typeof target === 'function';
    const sWarning = !bTargetIsFunction ? '[not function] ' : '';
    const result = bTargetIsFunction ? target(...aArguments) : target;
    return {
      sWarning,
      result,
    };
  }

  renderExecutionResult(welParent, sCommand, sWarning, result) {
    const welResult = $(`
            <div class="_debug_console_box">
                <p>${sCommand}</p>
                <p class="ee">${sWarning}</p>
            </div>`);
    /* eslint no-new: 0 */
    new DebugObject(welResult, result);
    welParent.append(welResult);
  }

  /**
   * 끝에 ()이 있으면 함수 실행 명령으로 본다. 체이닝은 지원 안함
   * 만약의 경우를 대비해서 만듦
   * @param sCommand
   * @returns {string}
   */
  getExecutionType(sCommand) {
    const { sLastCommand } = this.separateInput(sCommand);
    let bFunctionCall = false;
    if (sLastCommand.indexOf('(') !== -1 && sLastCommand.endsWith(')')) {
      bFunctionCall = true;
    }
    return bFunctionCall ? 'function' : 'search';
  }
}
